'use client';

import { c as _c } from "react/compiler-runtime";
import useRole from '@shared/utils/hooks/useRole';
import { HardDrive } from 'lucide-react';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import { client } from '../../utils/trpc';
import Button from '../common/Button';
import Modal, { ModalContent, ModalHeader } from '../HOC/Modal';
import { useConnection, VaultAiNotice } from './common';
import ResetVaultButton from './ResetVaultButton';

// Lazy: this button renders on every profile, while the panels behind it —
// the folder picker, the vault search and the whole research flow — only load
// once an admin opens the modal.
const EmbedPanel = dynamic(() => import('./EmbedPanel'), {
  ssr: false
});
const SearchPanel = dynamic(() => import('./SearchPanel'), {
  ssr: false
});
const UploadPanel = dynamic(() => import('./UploadPanel'), {
  ssr: false
});
const FactiiisPanel = dynamic(() => import('./FactiiisPanel'), {
  ssr: false
});
const VaultResearchPanel = dynamic(() => import('./VaultResearchPanel'), {
  ssr: false
});
const ResearchPanel = dynamic(() => import('../pages/ResearchPanel'), {
  ssr: false
});

// "OneDrive" on the owner's own profile: the whole vault in one modal, so
// nothing here means leaving the page.
//
// Six tabs over the same panels the /onedrive route renders. One UI, two
// places to reach it. Ask used to be a pill in the global search bar; it moved
// here because the thing it searches is this vault, not the site, and having it
// in the header implied otherwise.
//
// Admin only for now — the same gate the router enforces. Syncing ships private
// files to a model provider, so it isn't open to every account yet.
//
// Shown whether or not OneDrive is connected: connecting happens inside the
// modal, so this is the one entry point rather than sending people to Settings
// and back. It deliberately does NOT query connection status — the label is the
// same either way, and firing that request from every profile render just to
// decide a word is wasted.
//
// Own modal state, deliberately. ProfileSection already carries eight useState
// and 400+ lines; a self-contained button keeps this feature out of it.

const TABS = [{
  id: 'ask',
  label: 'Ask'
}, {
  id: 'upload',
  label: 'Upload'
}, {
  id: 'search',
  label: 'Search'
}, {
  id: 'sync',
  label: 'Sync'
}, {
  id: 'factiiis',
  label: 'Factiiis'
}, {
  id: 'research',
  label: 'Research'
}] as const;
type Tab = (typeof TABS)[number]['id'];
const OneDriveSyncButton = () => {
  const $ = _c(11);
  const {
    isAdmin,
    isPremium
  } = useRole(client);
  const canUseVault = isPremium || isAdmin;
  const {
    connected,
    status
  } = useConnection(canUseVault);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("ask");
  const [askOpened, setAskOpened] = useState(true);
  if (!canUseVault) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setOpen(false);
      setTab("ask");
      setAskOpened(true);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const closeHandler = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = next => {
      setTab(next);
      if (next === "ask") {
        setAskOpened(true);
      }
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectTab = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Button text="OneDrive" variant="CLEAR" classNames="px-2.5 py-1 text-xs" icon={<HardDrive height={13} width={13} />} onClick={() => setOpen(true)} />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== askOpened || $[4] !== connected || $[5] !== open || $[6] !== status || $[7] !== tab) {
    t3 = open && <Modal closeHandler={closeHandler} maxWidth={860}><ModalHeader closeHandler={closeHandler}><span className="flex w-full items-center justify-between pr-3">OneDrive{connected && <ResetVaultButton />}</span></ModalHeader>{!status.isLoading && !connected ? <ModalContent><EmbedPanel /></ModalContent> : <><div className="flex shrink-0 gap-1 border-b border-ds-border-subtle px-4">{TABS.map(t => <button key={t.id} type="button" onClick={() => selectTab(t.id)} className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${tab === t.id ? "border-primary text-textPrimary" : "border-transparent text-textSecondary hover:text-textPrimary"}`}>{t.label}</button>)}</div>{askOpened && <div className={tab === "ask" ? "h-[min(36rem,calc(90vh-11rem))]" : "hidden"}><ResearchPanel inline={true} hideHeader={true} query="" source="vault" closeHandler={closeHandler} /></div>}{tab !== "ask" && <ModalContent lockHeight={true}>{(tab === "upload" || tab === "sync") && <div className="mb-4"><VaultAiNotice /></div>}{tab === "search" && <SearchPanel />}{tab === "upload" && <UploadPanel />}{tab === "sync" && <EmbedPanel />}{tab === "factiiis" && <FactiiisPanel />}{tab === "research" && <VaultResearchPanel />}</ModalContent>}</>}</Modal>;
    $[3] = askOpened;
    $[4] = connected;
    $[5] = open;
    $[6] = status;
    $[7] = tab;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
export default OneDriveSyncButton;